// 每日學習活動：users/{uid}/activity/{YYYY-MM-DD}，每天一筆，作答時累加
// 用途：學習分析的「日曆」分頁（選某一天看當天做了什麼）。從上線日起累積，之前的日子沒有逐題明細。
// 訪客存在 localStorage（gsat_activity）
import { arrayUnion, collection, doc, getDocs, increment, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { QRef, answerKey } from './answerLog';

export type DayAct = {
  a: number;                                   // 當天作答題數
  c: number;                                   // 其中答對題數
  by: Record<string, { a: number; c: number }>; // 各科目
  w: string[];                                 // 當天答錯過的題目 key（科目__主題__題目ID）
  st?: Record<string, { card?: number; map?: number; match?: number; mw?: number }>;   // 各科目：閃卡張數、心智圖次數、配對成功對數、配對錯誤次數
  dict?: number;                              // 當天字典查字次數
  stroke?: number;                            // 當天筆順查了幾個字
  sec: number;                                 // 當天在 App 內的學習秒數（前景且有操作才計）
};
export type ActivityMap = Record<string, DayAct>;

const LS_KEY = 'gsat_activity';

// 本地日期（台灣時區）YYYY-MM-DD；不用 toISOString，避免凌晨被算成前一天
export const dayKey = (d: Date = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const readLocal = (): ActivityMap => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; }
};

export async function logActivity(uid: string | null, ref: QRef, correct: boolean) {
  const day = dayKey();
  if (!uid) {
    try {
      const m = readLocal();
      const cur = m[day] ?? { a: 0, c: 0, by: {}, w: [], sec: 0 };
      const by = cur.by[ref.s] ?? { a: 0, c: 0 };
      const k = answerKey(ref);
      m[day] = { sec: cur.sec ?? 0, a: cur.a + 1, c: cur.c + (correct ? 1 : 0),
        by: { ...cur.by, [ref.s]: { a: by.a + 1, c: by.c + (correct ? 1 : 0) } },
        w: correct || cur.w.includes(k) ? cur.w : [...cur.w, k] };
      localStorage.setItem(LS_KEY, JSON.stringify(m));
    } catch { /* 忽略 */ }
    return;
  }
  await setDoc(doc(db, 'users', uid, 'activity', day), {
    day,
    a: increment(1),
    c: increment(correct ? 1 : 0),
    by: { [ref.s]: { a: increment(1), c: increment(correct ? 1 : 0) } },
    ...(correct ? {} : { w: arrayUnion(answerKey(ref)) }),
    ts: serverTimestamp(),
  }, { merge: true });
}

export async function loadActivity(uid: string | null): Promise<ActivityMap> {
  if (!uid) return readLocal();
  const snap = await getDocs(collection(db, 'users', uid, 'activity'));
  const map: ActivityMap = {};
  snap.docs.forEach(d => {
    const v = d.data() as Partial<DayAct>;
    map[d.id] = { a: v.a ?? 0, c: v.c ?? 0, by: v.by ?? {}, w: v.w ?? [], sec: v.sec ?? 0, st: (v as any).st ?? {}, dict: (v as any).dict ?? 0, stroke: (v as any).stroke ?? 0 };
  });
  return map;
}

// ── 學習時長 ────────────────────────────────────────────────────────────
// 頁面在前景且 60 秒內有操作（點擊、按鍵、捲動、觸控）才計時，每 5 秒累加；
// 累積滿 60 秒、或離開頁面時寫入當天紀錄。登入狀態由 App 通知（setStudyUid）。
const TICK = 5, IDLE_MS = 60_000, FLUSH_SEC = 60;
let studyUid: string | null | undefined;   // undefined＝登入狀態尚未確定，先不計時
let pendingSec = 0;
let lastActive = 0;
let timerStarted = false;

export const getStudyUid = (): string | null => studyUid ?? null;

export function setStudyUid(uid: string | null) {
  if (studyUid !== undefined && studyUid !== uid) flushStudy();   // 換帳號前先把舊帳號的時間寫掉
  studyUid = uid;
}

export function flushStudy() {
  const sec = pendingSec; pendingSec = 0;
  if (sec <= 0 || studyUid === undefined) return;
  const uid = studyUid, day = dayKey();
  if (!uid) {
    try {
      const m = readLocal();
      const cur = m[day] ?? { a: 0, c: 0, by: {}, w: [], sec: 0 };
      m[day] = { ...cur, sec: (cur.sec ?? 0) + sec };
      localStorage.setItem(LS_KEY, JSON.stringify(m));
    } catch { /* 忽略 */ }
    return;
  }
  setDoc(doc(db, 'users', uid, 'activity', day), { day, sec: increment(sec), ts: serverTimestamp() }, { merge: true }).catch(() => {});
}

export function startStudyTimer() {
  if (timerStarted || typeof window === 'undefined') return;
  timerStarted = true;
  lastActive = Date.now();
  const touch = () => { lastActive = Date.now(); };
  for (const ev of ['pointerdown', 'keydown', 'scroll', 'touchstart']) window.addEventListener(ev, touch, { passive: true, capture: true });
  setInterval(() => {
    if (studyUid === undefined || document.visibilityState !== 'visible' || Date.now() - lastActive > IDLE_MS) return;
    pendingSec += TICK;
    if (pendingSec >= FLUSH_SEC) flushStudy();
  }, TICK * 1000);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flushStudy(); });
  window.addEventListener('pagehide', flushStudy);
}

export const fmtDur = (sec: number) => {
  const m = Math.round(sec / 60);
  if (sec > 0 && m < 1) return '不到 1 分鐘';
  return m < 60 ? `${m} 分鐘` : `${Math.floor(m / 60)} 小時${m % 60 ? ` ${m % 60} 分` : ''}`;
};

// 閃卡、心智圖、配對的學習紀錄：不屬於逐題作答，另外累計到當天（card＝翻到背面的閃卡張數，map＝看心智圖次數，match＝配對成功的對數，mw＝配對選錯次數）
export async function logStudy(uid: string | null, sub: string, kind: 'card' | 'map' | 'match' | 'mw') {
  const day = dayKey();
  if (!uid) {
    try {
      const m = readLocal();
      const cur = m[day] ?? { a: 0, c: 0, by: {}, w: [], sec: 0 };
      const st = { ...(cur.st ?? {}) };
      st[sub] = { ...(st[sub] ?? {}), [kind]: ((st[sub] ?? {})[kind] ?? 0) + 1 };
      m[day] = { ...cur, st };
      localStorage.setItem(LS_KEY, JSON.stringify(m));
    } catch { /* 忽略 */ }
    return;
  }
  await setDoc(doc(db, 'users', uid, 'activity', day), { day, st: { [sub]: { [kind]: increment(1) } }, ts: serverTimestamp() }, { merge: true });
}

// 字典、筆順查字：不分科目，累計到當天（dict＝查字次數，stroke＝筆順查了幾個不重複的字）
export function logLookup(kind: 'dict' | 'stroke', n = 1) {
  if (studyUid === undefined || n <= 0) return;   // 登入狀態未確定時不記
  const uid = studyUid, day = dayKey();
  if (!uid) {
    try {
      const m = readLocal();
      const cur = m[day] ?? { a: 0, c: 0, by: {}, w: [], sec: 0 };
      m[day] = { ...cur, [kind]: (cur[kind] ?? 0) + n };
      localStorage.setItem(LS_KEY, JSON.stringify(m));
    } catch { /* 忽略 */ }
    return;
  }
  setDoc(doc(db, 'users', uid, 'activity', day), { day, [kind]: increment(n), ts: serverTimestamp() }, { merge: true }).catch(() => {});
}
